import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  User, 
  Building2, 
  Award,
  ChevronDown,
  Phone,
  Mail
} from 'lucide-react';
import { useLanguageStore } from '../store/useLanguageStore';

const HierarchySection: React.FC = () => {
  const { t } = useLanguageStore();
  const [isExpanded, setIsExpanded] = useState(false);

  const departments = [
    'Dental Surgeon', 'Technical Service', 'PHID Health Service', 'Dispensary',
    'Pharmacist', 'MLT', 'Nursing Officer', 'PHM', 'Attendant', 'Secretary',
    'SS/Updt', 'Midwifery', 'Outpatient', 'Dispensation Ethics Officer',
    'Management Assistant', 'Field Officer', 'Driver', 'Cook'
  ];

  const departmentsSinhala = [
    'දන්ත ශල්‍ය වෛද්‍යවරයා', 'තාක්ෂණික සේවාව', 'PHID සෞඛ්‍ය සේවාව', 'ඖෂධාගාරය',
    'ඖෂධවේදියා', 'වෛද්‍ය රසායනාගාර තාක්ෂණවේදියා', 'හෙද නිලධාරිනිය', 'මහජන සෞඛ්‍ය මිඩ්වයිෆ්', 'සේවක', 'ලේකම්',
    'SS/Updt', 'වීදිකාව', 'බාහිර රෝගී', 'ඖෂධ දීමනා ආචාර ධර්ම නිලධාරි',
    'කළමනාකරණ සහායක', 'ක්ෂේත්‍ර නිලධාරි', 'රියදුරු', 'කුක්'
  ];

  return (
    <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 via-blue-50/30 to-green-50/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-8 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mb-4 md:mb-6">
            <Building2 className="w-10 h-10 md:w-12 md:h-12 text-primary-green" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-text-dark text-center">
              {t('organizational-hierarchy', 'Organizational Hierarchy', 'ආයතනික ධුරාවලිය')}
            </h2>
          </div>
          <div className="w-24 md:w-32 h-1 bg-gradient-to-r from-primary-green to-secondary-blue mx-auto rounded-full" />
        </motion.div>

        {/* Main Content - Limited width for better readability */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 sm:p-6 md:p-8">
            {/* DMO Section */}
            <motion.div
              className="mb-6 md:mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div 
                className="bg-gradient-to-r from-primary-green/10 to-secondary-blue/10 rounded-xl p-4 sm:p-5 md:p-6 cursor-pointer"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <div className="flex items-start sm:items-center justify-between gap-3">
                  <div className="flex items-start sm:items-center gap-3 md:gap-4 flex-1">
                    <Award className="w-8 h-8 md:w-10 md:h-10 text-primary-green flex-shrink-0 mt-1 sm:mt-0" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-text-dark break-words">
                        {t('dmo', 'DMO - District Medical Officer', 'DMO - දිස්ත්‍රික් වෛද්‍ය නිලධාරි')}
                      </h3>
                      <p className="text-sm md:text-base text-text-secondary">Dr. P. Weeraketiya</p>
                    </div>
                  </div>
                  <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} className="flex-shrink-0">
                    <ChevronDown className="w-5 h-5 md:w-6 md:h-6 text-gray-500" />
                  </motion.div>
                </div>
                
                <div className="mt-3 md:mt-4 flex flex-col sm:flex-row gap-3 sm:gap-6 text-xs sm:text-sm text-text-secondary">
                  <a 
                    href="tel:+94472246260" 
                    className="flex items-center gap-2 hover:text-primary-green transition-colors duration-300"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Phone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="break-all">+94 47 224 6260</span>
                  </a>
                  <a 
                    href="mailto:dmo.weeraketiya@health.gov.lk" 
                    className="flex items-center gap-2 hover:text-primary-green transition-colors duration-300"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Mail className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="break-all">dmo.weeraketiya@health.gov.lk</span>
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Departments */}
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-4 md:mt-6"
              >
                <h4 className="text-base md:text-lg font-semibold text-text-dark mb-3 md:mb-4">
                  {t('departments', 'Departments & Units', 'දෙපාර්තමේන්තු සහ ඒකක')}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3">
                  {departments.map((dept, index) => (
                    <motion.div
                      key={dept}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-gray-50 rounded-lg p-2.5 md:p-3 flex items-center gap-2 md:gap-3"
                    >
                      <User className="w-4 h-4 md:w-5 md:h-5 text-secondary-blue flex-shrink-0" />
                      <span className="text-xs sm:text-sm font-medium text-text-dark break-words">
                        {t(`dept-${dept.toLowerCase().replace(/\s+/g, '-')}`, dept, departmentsSinhala[index])}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HierarchySection;
